/**
 * Email: the mailbox password reset links are sent from (spec 20).
 *
 * An admin connects a Zoho Mail mailbox with Zoho's own consent screen. The
 * panel never sees a password or a token: the browser goes to Zoho, Zoho sends
 * it back to `EmailCallbackScreen`, and the API keeps the refresh token
 * encrypted.
 *
 * The screen has three states:
 * - the server isn't configured, which only ops can fix, so it names the env vars;
 * - configured but not connected, which shows a Connect button;
 * - connected, which shows the sending address, the last send or error, a test
 *   send, and Disconnect.
 */

import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Badge,
  Button,
  Card,
  CardHeader,
  Definition,
  DefinitionList,
  ErrorNotice,
  Page,
  SuccessNotice,
} from "../components/primitives";
import {
  disconnectMailbox,
  fetchMailStatus,
  sendTestEmail,
  startZohoConnect,
  type MailStatus,
} from "../lib/api/mail";
import { formatDateTime } from "../lib/format";
import { confirmAction, useMutation, useQuery } from "../lib/hooks";

export function EmailSettingsScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  // The callback screen hands over its success sentence in router state.
  const [notice, setNotice] = useState<string | null>(
    (location.state as { notice?: string } | null)?.notice ?? null,
  );

  const { data, error, loading, reload } = useQuery<MailStatus>(() => fetchMailStatus(), []);

  const connect = useMutation(() => startZohoConnect(), {
    onSuccess: ({ authorize_url }) => window.location.assign(authorize_url),
  });
  const test = useMutation(() => sendTestEmail(), {
    onSuccess: (result) => {
      setNotice(result.message);
      reload();
    },
  });
  const disconnect = useMutation(() => disconnectMailbox(), {
    onSuccess: (result) => {
      setNotice(result.message);
      reload();
    },
  });

  function dismissNotice() {
    setNotice(null);
    // Drop the router state too, so a refresh doesn't bring the notice back.
    navigate(location.pathname, { replace: true, state: null });
  }

  if (error && !data) {
    return (
      <Page>
        <ErrorNotice error={error} onRetry={reload} />
      </Page>
    );
  }

  if (loading && !data) {
    return (
      <Page>
        <Card>
          <div className="stack" style={{ gap: 12 }}>
            <div className="skeleton" style={{ height: 18, width: "30%" }} />
            <div className="skeleton" style={{ height: 40 }} />
          </div>
        </Card>
      </Page>
    );
  }

  if (!data) return null;

  const actionError = connect.error ?? test.error ?? disconnect.error;

  return (
    <Page>
      {notice ? <SuccessNotice message={notice} onDismiss={dismissNotice} /> : null}
      {actionError ? <ErrorNotice error={actionError} /> : null}

      <Card>
        <CardHeader
          title="Password reset email"
          hint="Members who forget their password get a reset link at the email address on their account. It is sent from the mailbox connected here."
          actions={
            data.connected ? (
              data.needs_reconnect ? (
                <Badge tone="danger">Reconnect needed</Badge>
              ) : (
                <Badge tone="active">Connected</Badge>
              )
            ) : (
              <Badge tone="muted">Not connected</Badge>
            )
          }
        />

        {!data.configured ? (
          <div className="stack" style={{ gap: 8 }}>
            <p style={{ margin: 0 }}>
              Email sending isn't set up on the server yet, so reset emails are not being sent.
            </p>
            <p className="text-small text-meta" style={{ margin: 0 }}>
              Whoever runs the server needs to set FITZONE_ZOHO_CLIENT_ID,
              FITZONE_ZOHO_CLIENT_SECRET and FITZONE_ZOHO_REDIRECT_URI, then restart the API.
              See DEPLOYMENT.md.
            </p>
          </div>
        ) : !data.encryption_configured && !data.connected ? (
          <div className="stack" style={{ gap: 8 }}>
            <p style={{ margin: 0 }}>
              The server can't store a mailbox connection securely yet, so no mailbox can be connected.
            </p>
            <p className="text-small text-meta" style={{ margin: 0 }}>
              Whoever runs the server needs to set FITZONE_TOKEN_ENCRYPTION_KEYS to a Fernet key
              (see api/.env.example), then restart the API.
            </p>
          </div>
        ) : !data.connected ? (
          <div className="stack" style={{ gap: 12 }}>
            <p style={{ margin: 0 }}>
              No mailbox is connected, so reset emails are not being sent. Connect the Zoho Mail
              mailbox the gym wants these emails to come from. You'll sign in to Zoho and approve
              access, then come back here.
            </p>
            <div className="row row--tight">
              <Button variant="accent" disabled={connect.pending} onClick={() => void connect.run()}>
                {connect.pending ? "Opening Zoho…" : "Connect Zoho Mail"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="stack" style={{ gap: 16 }}>
            {data.needs_reconnect ? (
              <div className="alert" role="alert">
                <span className="alert__title">
                  Zoho stopped accepting this connection, so reset emails are not being sent.
                </span>
                <span className="alert__action">
                  Reconnect the mailbox. This can happen after the mailbox password changes or access is revoked in Zoho.
                </span>
              </div>
            ) : null}

            <DefinitionList>
              <Definition term="Sending from">{data.from_address}</Definition>
              <Definition term="Connected">
                {data.connected_at
                  ? `${formatDateTime(data.connected_at)}${data.connected_by_name ? ` by ${data.connected_by_name}` : ""}`
                  : null}
              </Definition>
              <Definition term="Last email sent">
                {data.last_sent_at ? formatDateTime(data.last_sent_at) : "None yet"}
              </Definition>
              <Definition term="Last problem">
                {data.last_error
                  ? `${data.last_error}${data.last_error_at ? ` (${formatDateTime(data.last_error_at)})` : ""}`
                  : null}
              </Definition>
            </DefinitionList>

            <div className="row row--tight">
              {data.needs_reconnect ? (
                <Button variant="accent" disabled={connect.pending} onClick={() => void connect.run()}>
                  {connect.pending ? "Opening Zoho…" : "Reconnect"}
                </Button>
              ) : (
                <Button disabled={test.pending} onClick={() => void test.run()}>
                  {test.pending ? "Sending…" : "Send me a test email"}
                </Button>
              )}
              {!data.needs_reconnect ? (
                <Button variant="quiet" disabled={connect.pending} onClick={() => void connect.run()}>
                  Use a different mailbox
                </Button>
              ) : null}
              <Button
                variant="danger"
                disabled={disconnect.pending}
                onClick={() => {
                  if (
                    confirmAction(
                      "Disconnect this mailbox? Members will stop receiving password reset emails until another one is connected.",
                    )
                  ) {
                    void disconnect.run();
                  }
                }}
              >
                {disconnect.pending ? "Disconnecting…" : "Disconnect"}
              </Button>
            </div>
          </div>
        )}
      </Card>

      <p className="notice">
        Connecting and disconnecting are recorded in the audit log against your account. The
        mailbox's Zoho authorisation is stored encrypted and is never shown here.
      </p>
    </Page>
  );
}
